import React, { useMemo, Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FullStory } from '@fullstory/browser';

import { Text } from 'shared/components/Text/Text';
import useOnboardingStep from 'hooks/useOnboardingStep';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { useSetOnboardingMilestone } from 'api/agreement';
import { Button } from '../../../../../shared/components/Button/Button';
import {
  ContentItem,
  ContentList,
  MultipleAgreementContainer,
  OnboardingFooter,
  OnboardingIntroductionContainer,
  OnboardingIntroductionContent,
  SubmitButtonContainer,
} from './OnboardingIntroduction.styles';
import { OnboardingStep } from '../../../../types/util';
import { useUser } from '../../../../../hooks/useUser';
import { useFeatureFlags } from '../../../../../hooks/useFeatureFlags';
import { FlagKey, OnboardingMilestone } from '../../../../../api/types';
import {
  RadioButtonGroup,
  TRadioOption,
} from '../../../../../shared/components/inputs/RadioButtonGroup/RadioButtonGroup';
import { getDashboardRedirectPath } from '../../../../auth/Auth.utils';
import { TextDivider } from '../../../screens/ReviewAgreement/ReviewAgreement.styles';
import { pages } from '../../../../../pages';

const HouseGraphic = React.lazy(
  () => import('modules/status/components/IntroHouseGraphic/IntroHouseGraphic'),
);

export function OnboardingIntroduction({ onShowBanner }: { onShowBanner: () => void }) {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.introduction' });
  const { goToStep } = useOnboardingStep();

  const navigate = useNavigate();
  const { userResult, selectedAgreement, setSelectedAgreement, refetch: refetchUser } = useUser();

  const { mutateAsync: setOnboardingMilestone } = useSetOnboardingMilestone();

  const {
    flags: { [FlagKey.onboardingRefresh]: onboardingRefresh },
  } = useFeatureFlags();

  const userAgreementAddressOptions: TRadioOption[] = useMemo(() => {
    const optionsArray: { id: string; value: string; label: string }[] = [];
    userResult?.user?.agreements.forEach((agreement) => {
      optionsArray.push({
        id: agreement.id,
        value: agreement.id,
        label: t('addressFormat', {
          street_1: agreement.system.address.street_1,
          street_2: agreement.system.address.street_2
            ? ` ${agreement.system.address.street_2}`
            : '',
          city: agreement.system.address.city,
          state: agreement.system.address.state,
        }),
      });
    });
    return optionsArray;
  }, [userResult, t]);

  const bodyList: string[] = t('body', {
    returnObjects: true,
  }) as string[];

  const handleClick = async () => {
    if (selectedAgreement && !selectedAgreement.onboarding_milestone) {
      await setOnboardingMilestone({
        agreement_id: selectedAgreement.id,
        body: { milestone: OnboardingMilestone.PRE_INSTALLATION },
      });
      refetchUser();
    }

    FullStory('trackEvent', {
      name: 'Onboarding - Clicked Get Started Button',
      properties: {
        page: 'Status',
        onboardingStep: 'Introduction',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });

    onShowBanner();
    goToStep(OnboardingStep.REVIEW_AGREEMENT);
  };

  return (
    <OnboardingIntroductionContainer>
      <Suspense fallback={<LoadingSpinner />}>
        <HouseGraphic />
      </Suspense>
      <Text as="h1" $textAlign="center" $fontWeight={700}>
        {t('title')}
      </Text>

      <OnboardingIntroductionContent>
        <ContentList>
          {Array.isArray(bodyList) &&
            bodyList.map((item) => (
              <ContentItem key={item}>
                <Text as="p">{item}</Text>
              </ContentItem>
            ))}
        </ContentList>

        {userAgreementAddressOptions.length > 1 && (
          <MultipleAgreementContainer>
            <TextDivider>
              <Text as="h2" $textAlign="center" $fontWeight={600}>
                {t('multipleAgreement.title')}
              </Text>
            </TextDivider>

            <Trans>
              <Text as="p">{t('multipleAgreement.description')}</Text>
            </Trans>

            <RadioButtonGroup
              id={`multiple-agreement-user-${userResult?.user?.id}`}
              name={`multiple-agreement-user-${userResult?.user?.id}`}
              onChange={(_, value) => {
                if (value !== selectedAgreement?.id) {
                  const newAgreement = userResult?.user?.agreements.find(({ id }) => id === value);
                  setSelectedAgreement(newAgreement);
                  const { hasDashboardAccess } = getDashboardRedirectPath(
                    userResult?.user,
                    newAgreement,
                    onboardingRefresh,
                  );
                  if (hasDashboardAccess) {
                    navigate(`/${pages.DASHBOARD}`);
                  }
                }
              }}
              value={selectedAgreement?.id}
              options={userAgreementAddressOptions}
              margin="20px 0"
            />
          </MultipleAgreementContainer>
        )}
      </OnboardingIntroductionContent>
      <OnboardingFooter>
        <SubmitButtonContainer>
          <Button label={t('button')} onClick={handleClick} isFullWidth />
        </SubmitButtonContainer>
      </OnboardingFooter>
    </OnboardingIntroductionContainer>
  );
}

export default OnboardingIntroduction;
